import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { Navigate } from '~/utils/navigation';
import { TracedRoutes } from '@comeon/error-reporter';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectFranchiseId as selectAccountFranchiseId,
	selectTestPlayer,
	selectCurrency
} from '~/components/account/ducks/index.js';
import { selectFranchiseId, selectCasinoMaintenanceEnabled } from '~/components/brand/ducks/selectors.js';
import ProductRedirect from '~/components/brand/product-redirect.js';
import { invalidateCasinoData } from '~/components/casino/ducks/index';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import ScrollToTop from '~/utils/scroll-to-top';
import Game from '~/components/casino-shared/game-async';
import NullLoader from '~/components/null-loader.js';
import { getRelativePaths } from '~/utils/pathname';
import ExploreSkeleton from '~/components/casino-shared/explore/explore-skeleton';
import GamesGridSkeleton, { GamesGridSkeletonContainer } from '~/components/casino-shared/styles/games-grid-skeleton';
import { selectAuthenticated } from '~/components/authentication/ducks/selectors';
import CasinoSeoHeaderContainer from '~/components/casino/casino-routes/casino-seo-header-container';
import { closeLiveCasinoFeedSocket, establishLiveCasinoFeedSocketConnection } from '~/components/live-casino/ducks';
import { isNativeCasinoWebSocketEnabled } from '~/components/app-bridge/app-properties.js';

// lazy imports to reduce initial load of casino/explore page
const ProductMaintenance = lazy(
	() =>
		import(
			/* webpackChunkName: "product-maintenance" */
			'~/components/utilities/product-maintenance.js'
		)
);

const Explore = lazy(
	() =>
		import(
			/* webpackChunkName: "shared-explore" */
			'~/components/casino-shared/explore/explore'
		)
);

const MyGames = lazy(
	() =>
		import(
			/* webpackChunkName: "my-games" */
			'~/components/casino-shared/my-games/my-games'
		)
);

const AllGames = lazy(
	() =>
		import(
			/* webpackChunkName: "all-games" */
			'~/components/casino-shared/all-games/all-games'
		)
);

const ProviderGames = lazy(
	() =>
		import(
			/* webpackChunkName: "provider-games" */
			'~/components/casino-shared/provider/provider'
		)
);

const SubProviderGames = lazy(
	() =>
		import(
			/* webpackChunkName: "provider-games" */
			'~/components/casino-shared/subprovider/subprovider'
		)
);

const SubmenuGames = lazy(() => import('~/components/casino-shared/subnavigation/games'));

const ConnectedSearchResults = lazy(() => import('~/components/search/search-results-container'));

const ExclusiveGameWidgetNotifications = lazy(
	() => import('~/components/casino-shared/exclusive-game-widget/notification/notification')
);

const ExploreProduct = () => (
	<ProductRedirect product="casino" triggerMainContentLoading={false}>
		<Suspense fallback={<ExploreSkeleton />}>
			<Explore />
		</Suspense>
	</ProductRedirect>
);

const MyGamesProduct = () => (
	<ProductRedirect product="casino">
		<MyGames />
	</ProductRedirect>
);

const AllGamesProduct = () => (
	<ProductRedirect product="casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<AllGames />
		</Suspense>
	</ProductRedirect>
);

const ProviderGamesProduct = () => (
	<ProductRedirect product="casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<ProviderGames />
		</Suspense>
	</ProductRedirect>
);

const SubProviderGamesProduct = () => (
	<ProductRedirect product="casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<SubProviderGames />
		</Suspense>
	</ProductRedirect>
);

const SubMenuGamesProduct = () => (
	<ProductRedirect product="casino" triggerMainContentLoading={false}>
		<Suspense
			fallback={
				<GamesGridSkeletonContainer>
					<GamesGridSkeleton />
				</GamesGridSkeletonContainer>
			}
		>
			<SubmenuGames />
		</Suspense>
	</ProductRedirect>
);

const CasinoRoutes = () => {
	const dispatch = useDispatch();
	const testPlayer = useSelector(selectTestPlayer);
	const currency = useSelector(selectCurrency);
	const accountFranchiseId = useSelector(selectAccountFranchiseId);
	const franchiseId = useSelector(selectFranchiseId);
	const casinoMaintenanceEnabled = useSelector(selectCasinoMaintenanceEnabled);
	const location = useLocation();
	const authenticated = useSelector(selectAuthenticated);
	const {
		ROUTE_CASINO,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_SUBMENU,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_CASINO_PROVIDER,
		ROUTE_CASINO_GAME,
		ROUTE_CASINO_SEARCH,
		ROUTE_CASINO_SUBPROVIDER
	} = useCasinoRoutes();

	const paths = useMemo(() => {
		const CHILD_ROUTES = [
			ROUTE_CASINO,
			ROUTE_CASINO_ALL_GAMES,
			ROUTE_CASINO_EXPLORE,
			ROUTE_CASINO_SUBMENU,
			ROUTE_CASINO_MY_GAMES,
			ROUTE_CASINO_MY_GAMES_LOVED,
			ROUTE_CASINO_PROVIDER,
			ROUTE_CASINO_SUBPROVIDER,
			ROUTE_CASINO_GAME,
			ROUTE_CASINO_SEARCH
		];
		return getRelativePaths(CHILD_ROUTES, ROUTE_CASINO);
	}, [
		ROUTE_CASINO,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_GAME,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_CASINO_MY_GAMES_LOVED,
		ROUTE_CASINO_PROVIDER,
		ROUTE_CASINO_SEARCH,
		ROUTE_CASINO_SUBMENU,
		ROUTE_CASINO_SUBPROVIDER
	]);

	useEffect(() => {
		dispatch(invalidateCasinoData());
	}, [dispatch, accountFranchiseId, franchiseId, currency, testPlayer]);

	useEffect(() => {
		if (isNativeCasinoWebSocketEnabled()) return;
		dispatch(establishLiveCasinoFeedSocketConnection({ currency, franchiseId }));

		return () => {
			dispatch(closeLiveCasinoFeedSocket());
		};
	}, [dispatch, currency, franchiseId]);

	if (casinoMaintenanceEnabled) {
		return (
			<>
				<ScrollToTop />
				<TracedRoutes key="casino-maintenance-routes">
					<Route
						path={paths[ROUTE_CASINO_EXPLORE]}
						element={<ProductMaintenance titleKey="MAINTENANCE_TITLE" descKey="MAINTENANCE_SLOTS_DESC" />}
					/>
					<Route path="*" element={<Navigate to={ROUTE_CASINO_EXPLORE} />} />
				</TracedRoutes>
			</>
		);
	}

	return (
		<>
			<ScrollToTop />
			<CasinoSeoHeaderContainer />
			<Suspense fallback={<NullLoader />}>
				<TracedRoutes key="casino-routes">
					<Route path={paths[ROUTE_CASINO_EXPLORE]} element={<ExploreProduct />} />
					<Route path={paths[ROUTE_CASINO_SEARCH]} element={<ConnectedSearchResults />} />
					<Route path={paths[ROUTE_CASINO_ALL_GAMES]} element={<AllGamesProduct />} />
					<Route path={`${paths[ROUTE_CASINO_ALL_GAMES]}/:filter`} element={<AllGamesProduct />} />
					<Route path={`${paths[ROUTE_CASINO_SUBMENU]}/:submenu`} element={<SubMenuGamesProduct />} />
					<Route path={`${paths[ROUTE_CASINO_PROVIDER]}/:providerName`} element={<ProviderGamesProduct />} />
					<Route
						path={`${paths[ROUTE_CASINO_SUBPROVIDER]}/:subproviderCode`}
						element={<SubProviderGamesProduct />}
					/>
					<Route
						path={paths[ROUTE_CASINO_MY_GAMES]}
						element={<Navigate to={`${ROUTE_CASINO_MY_GAMES_LOVED}`} replace />}
					/>
					<Route path={`${paths[ROUTE_CASINO_MY_GAMES]}/:filter`} element={<MyGamesProduct />} />
					<Route path={`${paths[ROUTE_CASINO_GAME]}/:gameCode/:gameMode`} element={<Game />} />
					<Route
						path="*"
						element={
							// TODO (navigation) - use search params or redux for state
							<Navigate to={`${ROUTE_CASINO_EXPLORE}${location.search}`} replace state={location.state} />
						}
					/>
				</TracedRoutes>
				{authenticated && <ExclusiveGameWidgetNotifications />}
			</Suspense>
		</>
	);
};

export default CasinoRoutes;
